import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  Ban,
  BarChart3,
  Boxes,
  ChevronRight,
  BookOpen,
  ClipboardList,
  CreditCard,
  FileSearch2,
  FileSpreadsheet,
  History,
  KeyRound,
  LayoutDashboard,
  LocateFixed,
  LogOut,
  Moon,
  Network,
  Package,
  PackagePlus,
  PanelLeft,
  Plug,
  Radar,
  Scale,
  Search,
  ShoppingBag,
  SlidersHorizontal,
  Sun,
  TruckIcon,
  Users,
  Zap,
} from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";
import type { NavGroup, NavGroupId, NavItemIconId, NavSection } from "./nav.config";
import { NAV_GROUPS, SECTION_LABELS, getActiveGroupId, isNavPathActive } from "./nav.config";
import { useFleetMode } from "@/lib/tms/fleet-mode-context";
import { FLEET_MODE_LABELS, isNavGroupVisibleForFleetMode, isNavItemVisibleForFleetMode } from "@/lib/tms/fleet-mode";

const GROUP_ICONS: Record<NavGroupId, React.ComponentType<{ className?: string }>> = {
  dispatch: Zap,
  fleet: TruckIcon,
  exceptions: AlertTriangle,
  quotes: Search,
  shipments: Package,
  tracking: Radar,
  carriers: Network,
  templates: ClipboardList,
  addresses: BookOpen,
  analytics: BarChart3,
  account: KeyRound,
};

const ITEM_ICONS: Record<NavItemIconId, React.ComponentType<{ className?: string }>> = {
  quoteLtl: Scale,
  quoteParcel: ShoppingBag,
  quoteRetrieve: FileSearch2,
  shipmentList: Boxes,
  shipmentBook: PackagePlus,
  shipmentCancel: Ban,
  trackingSearch: LocateFixed,
  trackingRecent: History,
  carrierList: Network,
  templateList: ClipboardList,
  addressList: BookOpen,
  analyticsDashboard: LayoutDashboard,
  analyticsReports: FileSpreadsheet,
  accountKeys: KeyRound,
  accountTeam: Users,
  accountBilling: CreditCard,
  accountSettings: SlidersHorizontal,
  accountIntegrations: Plug,
  dispatchConsole: Zap,
  dispatchNewLoad: PackagePlus,
  fleetRegistry: TruckIcon,
  exceptionsQueue: AlertTriangle,
};

const SHIPPEEK_LOGO_URL = "/shippeek-mark.svg";

function SidebarLeaf({
  active,
  label,
  icon: Icon,
  isLast,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  isLast: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-stretch">
      {/* Tree connector */}
      <div className="relative w-4 shrink-0">
        {/* Vertical line from top to connector midpoint (or full height if not last) */}
        <div
          className={cn(
            "absolute left-0 top-0 w-px bg-border",
            isLast ? "h-1/2" : "h-full",
          )}
        />
        {/* Horizontal branch */}
        <div className="absolute left-0 top-1/2 h-px w-full bg-border" />
      </div>
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[13px] transition-colors duration-100",
          active
            ? "bg-accent text-black dark:text-white font-medium"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={onClick}
      >
        <Icon className="size-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
    </div>
  );
}

function SidebarGroup({
  group,
  defaultOpen,
  isActive,
  onNavigate,
}: {
  group: NavGroup;
  defaultOpen: boolean;
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const GroupIcon = GROUP_ICONS[group.id];

  React.useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-[13px] font-semibold transition-colors duration-100",
          open
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={() => setOpen((current) => !current)}
      >
        <GroupIcon className="size-3.5 shrink-0" />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronRight className={cn("size-3 text-muted-foreground/60 transition-transform duration-100", open && "rotate-90")} />
      </button>
      {open ? (
        <div className="ml-3 pl-0 mt-0.5 mb-1">
          {group.items.map((item, index) => {
            const ItemIcon = ITEM_ICONS[item.icon];

            return (
              <SidebarLeaf
                key={item.path}
                active={isActive(item.path)}
                label={item.label}
                icon={ItemIcon}
                isLast={index === group.items.length - 1}
                onClick={() => onNavigate(item.path)}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// Groups grouped by section, with section order preserved
const SECTION_ORDER: NavSection[] = ["operations", "rate_shopping", "tools", "account"];

export function AppSidebar({
  collapsed,
  onNavigate,
  onDisconnect,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
  onDisconnect: () => Promise<void>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { fleetMode, loading: fleetLoading } = useFleetMode();

  const activeGroupId = React.useMemo(() => getActiveGroupId(location.pathname), [location.pathname]);

  const isActive = React.useCallback(
    (path: string) => isNavPathActive(path, location.pathname, location.search),
    [location.pathname, location.search],
  );

  const goTo = React.useCallback(
    (path: string) => {
      navigate(path);
      onNavigate?.();
    },
    [navigate, onNavigate],
  );

  // Filter groups based on workspace mode
  const visibleGroups = React.useMemo<NavGroup[]>(() => {
    if (fleetLoading) return NAV_GROUPS;

    return NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => isNavItemVisibleForFleetMode(fleetMode, group.id, item.path)),
    })).filter((group) => isNavGroupVisibleForFleetMode(fleetMode, group.id) && group.items.length > 0);
  }, [fleetMode, fleetLoading]);

  // Group by visible section so dividers are based on rendered order.
  const visibleSections = React.useMemo(() => {
    const map = new Map<NavSection, NavGroup[]>();
    for (const section of SECTION_ORDER) {
      map.set(section, []);
    }
    for (const group of visibleGroups) {
      map.get(group.section)?.push(group);
    }

    return SECTION_ORDER.map((section) => ({
      section,
      groups: map.get(section) ?? [],
    })).filter(({ groups }) => groups.length > 0);
  }, [visibleGroups]);

  if (collapsed) {
    return (
      <div className="flex h-full flex-col bg-card text-foreground">
      <div className="flex h-12 items-center justify-center border-b border-border/60">
          <img src={SHIPPEEK_LOGO_URL} alt="ShipPeek" className="size-7 rounded-md" />
        </div>
        <nav className="flex-1 flex flex-col items-center gap-1 py-3">
          {visibleGroups.map((group) => {
            const Icon = GROUP_ICONS[group.id];
            const active = location.pathname.startsWith(group.basePath);
            return (
              <button
                key={group.id}
                type="button"
                title={group.label}
                className={cn(
                  "flex size-9 items-center justify-center rounded-lg transition-colors duration-100",
                  active
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                )}
                onClick={() => goTo(group.basePath)}
              >
                <Icon className="size-4" />
              </button>
            );
          })}
        </nav>
        <div className="border-t border-border/60 flex flex-col items-center gap-1 py-3">
          <button
            type="button"
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
            onClick={toggleTheme}
          >
            {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <button
            type="button"
            title="Sign out"
            className="flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
            onClick={() => void onDisconnect()}
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-card text-foreground overflow-hidden">
      {/* Brand */}
      <div className="shrink-0 border-b border-border/60 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <img src={SHIPPEEK_LOGO_URL} alt="ShipPeek" className="size-7 shrink-0 rounded-md" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">ShipPeek</p>
            {!fleetLoading ? (
              <span className="text-[10px] text-muted-foreground">
                {FLEET_MODE_LABELS[fleetMode]}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {visibleSections.map(({ section, groups }, sectionIndex) => {
          return (
            <React.Fragment key={section}>
              {sectionIndex > 0 ? (
                <hr className="my-2 border-border/40" />
              ) : null}
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
                {SECTION_LABELS[section]}
              </p>
              <div className="space-y-0.5">
                {groups.map((group) => (
                  <SidebarGroup
                    key={group.id}
                    group={group}
                    defaultOpen={activeGroupId === group.id}
                    isActive={isActive}
                    onNavigate={goTo}
                  />
                ))}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="shrink-0 border-t border-border/60 px-2 py-3 space-y-0.5">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          onClick={toggleTheme}
        >
          {theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
          <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
        </button>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => void onDisconnect()}
        >
          <LogOut className="size-3.5" />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}

export function SidebarTriggerButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="-ml-1">
      <Button aria-label="Toggle sidebar" variant="ghost" size="icon-sm" onClick={onClick}>
        <PanelLeft className="size-4" />
      </Button>
    </div>
  );
}
